import { Component, HostListener, OnDestroy, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { ProjectModel, StatusModel } from '../../../hub_schema/hubTypes';
import { FormControl, FormGroup } from '@angular/forms';
import { take, tap } from 'rxjs/operators';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { HubRecordEditorBase } from './_hub_record_editor_base';
import { AuthService } from '../../core/services/auth/auth.service';
import { ProjectLovService } from '../../core/services/project-lov.service';
import { Observable, Subscription, forkJoin, of } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { UnsavedChangesDialogComponent } from './unsaved-changes-dialog/unsaved-changes-dialog.component';
import { ErrorService } from '../../core/services/error.service';
import { DeleteConfirmationDialogComponent } from '../../shared/components/delete-confirmation-dialog/delete-confirmation-dialog.component';
import { HubMessageBoxComponent } from '../../shared/components/hub-message-box/hub-message-box.component';
import { SummaryEditComponent } from './summary-edit/summary-edit.component';
import { UserDataService } from 'src/app/core/services/user-data.service';
import { AppStateService } from '../../core/services/app-state.service';

@Component({
    selector: 'app-hub-record-edit-container',
    templateUrl: './hub-record-edit-container.component.html',
    styleUrls: ['./hub-record-edit-container.component.scss'],
    standalone: false
})
export class HubRecordEditContainerComponent extends HubRecordEditorBase implements OnInit, OnDestroy {
    // Dependencies
    private router: Router = inject(Router);
    private route: ActivatedRoute = inject(ActivatedRoute);
    private authService: AuthService = inject(AuthService);
    private userDataService: UserDataService = inject(UserDataService);
    private projectLovService: ProjectLovService = inject(ProjectLovService);
    private dialogService: MatDialog = inject(MatDialog);
    private errorService: ErrorService = inject(ErrorService);
    private changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
    private appStateService: AppStateService = inject(AppStateService);

    // #region Page Properties

    public get isPmChanges(): boolean {
        const urlParams = new URLSearchParams(window.location.search);
        const result = urlParams.get('pmchanges') === 'true';
        return result;
    }

    public get isSidebarExpanded(): boolean {
        return this.appStateService.isSidebarExpanded;
    }

    public isBusy: boolean = false;
    public savingState: string = '';
    public unauthorized: boolean = false;
    private routeChangedSubscription: Subscription;
    private sidebarExpandedChangedSubscription: Subscription;
    public isHeaderExpanded: boolean = false;

    public projectTabs: any = {
        summary: 'summary',
        team: 'team',
        location: 'location',
        relations: 'relations',
        strategicPillars: 'strategic-pillars',
        outcomes: 'outcomes',
        intermediate: 'intermediate',
        outputs: 'outputs',
        financials: 'financials',
        attributes: 'attributes',
        associatedFiles: 'associatedFiles',
        admin: 'admin'
    };

    public activeTab: string;

    public get isDirty(): boolean {
        return this.projectEditForm?.dirty || false;
    }

    // #endregion Page Properties

    // #region Data Properties

    public projectStatuses: StatusModel[];

    // #endregion Data Properties

    // #region Page Methods

    // This just prevents the ExpressionChangedAfterItHasBeenCheckedError console error
    public ngAfterViewChecked(): void {
        this.changeDetectorRef.detectChanges();
    }

    public ngOnInit(): void {
        this.routeChangedSubscription = this.route.params.subscribe((params: Params) => {
            const projectId = params['id'];
            this.activeTab = this.projectTabs.summary;
            this.isBusy = true;

            forkJoin({
                getProject: this.hubRecordEditService.getProject(projectId),
                projectStatuses: this.projectLovService.getProjectStatuses()
            })
                .pipe(take(1))
                .subscribe(results => {
                    if (this.project.isDeleted) {
                        this.router.navigateByUrl('/project-deleted');
                    }

                    this.calculateEffectiveWidth();
                    this.projectStatuses = results.projectStatuses;
                    this.isBusy = false;
                });
        });

        this.sidebarExpandedChangedSubscription = this.appStateService.isSidebarExpandedChanged.subscribe(() => {
            this.calculateEffectiveWidth();
        });
    }

    private calculateEffectiveWidth(): void {
        let effectiveWidth = window.innerWidth;

        if (this.isSidebarExpanded) {
            effectiveWidth -= 172;
        }

        if (this.project) {
            const projectNameLength = this.project.name.length;
            if (projectNameLength > 50) {
                const extraTextWidth = (projectNameLength - 50) * 9;
                effectiveWidth -= extraTextWidth;
            }
        }

        if (effectiveWidth < 1250) {
            this.isHeaderExpanded = true;
        } else {
            this.isHeaderExpanded = false;
        }
    }

    public activateTab(tab: string): void {
        setTimeout(() => {
            this.activeTab = tab;
        });
    }

    public showUnsavedChangesDialog(): Observable<boolean> {
        const dialogRef = this.dialogService.open(UnsavedChangesDialogComponent, {
            maxWidth: 'none'
        });
        return dialogRef.afterClosed();
    }

    // #endregion Page Methods

    // #region authorization methods

    public canUserDeleteRecord(): boolean {
        let isUserTeamMember = this.projectEditForm.controls.team.value.some(member => {
            return member.userId === this.userDataService.getUserId();
        });
        return this.authService.isAllowedToDeleteAsRegionOrDivisionEditor(this.project) || isUserTeamMember;
    }

    // #endregion authorization methods

    // #region Data / Form methods

    public getSaveButtonTooltip(): string {
        if (!this.isProjectValid || !this.isProjectNameValid) {
            return 'The form contains invalid data that must be corrected before the record can be saved.';
        } else if (!this.isDirty) {
            return 'No pending changes to save';
        }
        return 'save changes';
    }

    // #endregion Data / Form methods

    // #region Mutations

    public deleteHubRecordRequested(): void {
        const dialogRef = this.dialogService.open(DeleteConfirmationDialogComponent, {
            maxWidth: 'none',
            data: {
                warningMessage:
                    'You will not be able to recover this record. The record information will no longer display or rollup in in the Hub Application and the Hub Dashboards. If this information still needs to display and the work is no longer in progress, select Complete or Cancel under Change Record Status.'
            }
        });

        const deleteConfirmationDialog: DeleteConfirmationDialogComponent = dialogRef.componentInstance;

        deleteConfirmationDialog.actionConfirmed.pipe(take(1)).subscribe(() => {
            deleteConfirmationDialog.isBusy = true;

            this.hubRecordEditService
                .deleteProject()
                .pipe(take(1))
                .subscribe({
                    next: () => {
                        dialogRef.close();

                        const dialogRefMessageBox = this.dialogService.open(HubMessageBoxComponent, {
                            maxWidth: 'none',
                            data: {
                                title: 'Deleted',
                                message: `${this.project.name} has successfully been deleted.`,
                                confirmButtonText: 'Ok'
                            }
                        });

                        dialogRefMessageBox.afterClosed().subscribe(() => {
                            this.router.navigateByUrl('/portfolio');
                        });
                    },
                    error: err => {
                        this.errorService.addError(err, true);
                    }
                });
        });
    }

    public saveProject(): void {
        this.savingState = 'saving';

        this.hubRecordEditService
            .updateProject()
            .pipe(take(1))
            .subscribe({
                next: (savedProject: ProjectModel) => {
                    this.savingState = 'saved';

                    setTimeout(() => {
                        this.savingState = '';
                    }, 1500);
                },
                error: err => {
                    this.errorService.addError(err, true);
                    this.savingState = '';
                }
            });
    }

    // #endregion mutations

    @HostListener('window:beforeunload', ['$event'])
    public beforeUnloadHandler(event) {
        if (this.projectEditForm!.dirty) {
            event.returnValue = false;
            event.preventDefault();
            return false;
        }
    }

    @HostListener('window:resize', ['$event'])
    public afterResize(e) {
        this.calculateEffectiveWidth();
    }

    public ngOnDestroy(): void {
        this.projectEditForm = undefined;

        if (this.routeChangedSubscription) {
            this.routeChangedSubscription.unsubscribe();
        }

        if (this.sidebarExpandedChangedSubscription) {
            this.sidebarExpandedChangedSubscription.unsubscribe();
        }
    }
}
